<template>
	<slot v-if="!loaded"></slot>
	<DsBlock class="c-video-hero-top">
		<div v-if="isMobile" class="c-video-hero__content">
			<div class="c-video-hero__text">
				<DsTag
					v-if="vm.baseViewModel.tagText"
					class="c-video-hero__tag"
					:text="vm.baseViewModel.tagText"
				></DsTag>
				<DsText
					v-if="vm.baseViewModel.headline"
					html-element="h1"
					class="c-video-hero__title"
					text-type="heading-8xl"
					text-style="medium"
					>{{ vm.baseViewModel.headline }}<DsUnderscore></DsUnderscore
				></DsText>
			</div>
			<DsButton
				icon-start="button-play"
				variant="primary-inverted"
				:click-handler="() => switchToFullPlayback(true)"
				:disabled="playbackType === 'full'"
				data-testid="watchButton"
			>
				{{ vm.baseViewModel.ctaButtonLabel }}
			</DsButton>
		</div>
	</DsBlock>
	<div class="c-video-hero" ref="takeoverRef">
		<DsBlock background="default" class="c-video-hero__block">
			<div class="c-video-hero__container">
				<Transition v-if="isMobile === false" name="content">
					<div
						v-show="playbackType === 'bg' && isMobile === false"
						class="c-video-hero__content"
						:inert="playbackType === 'full'"
					>
						<div class="c-video-hero__text">
							<DsTag
								v-if="vm.baseViewModel.tagText"
								class="c-video-hero__tag"
								:text="vm.baseViewModel.tagText"
							></DsTag>
							<DsText
								v-if="vm.baseViewModel.headline"
								html-element="h1"
								class="c-video-hero__title"
								text-type="heading-8xl"
								text-style="medium"
								>{{ vm.baseViewModel.headline
								}}<DsUnderscore></DsUnderscore
							></DsText>
						</div>
						<DsButton
							icon-start="button-play"
							variant="primary-inverted"
							:click-handler="(_e) => switchToFullPlayback()"
							data-testid="watchButton"
						>
							{{ vm.baseViewModel.ctaButtonLabel }}
						</DsButton>
					</div>
				</Transition>

				<div class="c-video-hero__video-group">
					<Transition name="content">
						<DsButtonPlayProgress
							v-show="playbackType === 'bg'"
							class="c-video-hero__progress-button"
							:aria-label-play="vm.baseViewModel.playLabel"
							:playHandler="(_e: MouseEvent) => bgPlay()"
							:aria-label-pause="vm.baseViewModel.pauseLabel"
							:initialState="videoPlayState"
							:progress="videoProgress"
							:pauseHandler="(_e: MouseEvent) => bgPause()"
							ref="progressButton"
						></DsButtonPlayProgress>
					</Transition>

					<div
						class="c-video-hero__video-wrapper c-video-hero__video-wrapper--bg"
						data-testid="video-wrapper-bg"
						:class="dynamicBgVideoClass"
						:inert="playbackType === 'full'"
					>
						<div class="c-video-hero__video-overlay"></div>
						<vueVimeoPlayer
							data-testid="video-bg-player"
							tabindex="-1"
							class="c-video-hero__video"
							ref="vimeoBgPlayer"
							@ready="onBgReady"
							@playing="onPlaying"
							@timeupdate="timeupdateHandler"
							:videoUrl="`${vm.baseViewModel.backgroundVimeoVideo}`"
							:options="{
								autopause: true,
								autoplay: false,
								controls: false,
								dnt: true,
								loop: true,
								muted: true,
							}"
						></vueVimeoPlayer>
						<img
							v-if="vm.baseViewModel.image"
							:src="vm.baseViewModel.image"
							:srcset="`${vm.baseViewModel.image}?width=600 600w, ${vm.baseViewModel.image}?width=1200 1200w, ${vm.baseViewModel.image} 1800w`"
							:alt="vm.baseViewModel.headline"
							class="c-video-hero__poster-img"
						/>

					</div>

					<div
						class="c-video-hero__video-wrapper c-video-hero__video-wrapper--full"
						data-testid="video-wrapper-full"
						:class="dynamicFullVideoClass"
						:inert="playbackType === 'bg'"
					>
						<DsIconButton
							class="c-video-hero__close"
							:click-handler="showBackgroundVideo"
							:ariaLabel="vm.baseViewModel.closeLabel ?? 'Close'"
							icon-name="button-close"
							variant="ghost-inverted"
						></DsIconButton>
						<vueVimeoPlayer
							v-show="playbackType === 'full'"
							class="c-video-hero__vimeo"
							ref="vimeoPlayer"
							@ready="onReady"
							:videoUrl="`${vm.baseViewModel.vimeoVideo}`"
							:options="{
								autopause: true,
								background: false,
								controls: true,
								dnt: true,
								loop: false,
								muted: true,
								autoplay: false,
							}"
						></vueVimeoPlayer>
					</div>
				</div>
			</div>
		</DsBlock>
	</div>
</template>

<script setup lang="ts">
import {
	ComponentPublicInstance,
	computed,
	onMounted,
	onUnmounted,
	ref,
	watch,
} from 'vue';
import { vueVimeoPlayer } from 'vue-vimeo-player';
import {
	DsBlock,
	DsButton,
	DsButtonPlayProgress,
	DsIconButton,
	DsTag,
	DsText,
	DsUnderscore,
	dsUseIsMobile,
} from '@coloplast/design-system';

export type BaseViewModel = {
	headline?: string;
	tagText?: string;
	image?: string;
	vimeoVideo?: string;
	backgroundVimeoVideo?: string;
	ctaButtonLabel?: string;
	playLabel?: string;
	pauseLabel?: string;
	closeLabel?: string;
	currentBlockId?: number;
};

type VimeoPlayerInstance = InstanceType<typeof vueVimeoPlayer> &
	ComponentPublicInstance;

withDefaults(defineProps<{ vm: { baseViewModel: BaseViewModel } }>(), {
	vm: () => ({
		baseViewModel: {
			playLabel: 'Play',
			pauseLabel: 'Pause',
			closeLabel: 'Close',
		},
	}),
});

// Refs and state
const takeoverRef = ref<HTMLElement | null>(null);
const vimeoPlayer = ref<VimeoPlayerInstance | null>(null);
const vimeoBgPlayer = ref<VimeoPlayerInstance | null>(null);
const vimeoPlayerReady = ref(false);
const vimeoBgPlayerReady = ref(false);
const playbackType = ref<'bg' | 'full'>('bg');
const progressButton = ref<InstanceType<typeof DsButtonPlayProgress> | null>(
	null
);
const userPlayState = ref<'pause' | 'play' | undefined>('play');
const videoProgress = ref(0);
const videoPlayState = ref<'pause' | 'play' | undefined>('pause');
const hidePoster = ref(false);
const loaded = ref(false);

const { isMobile } = dsUseIsMobile(992);

// Computed props and class helper
const dynamicVideoClass = (
	playerReady: boolean,
	type: 'bg' | 'full',
	currentType: 'bg' | 'full',
	hidePoster: boolean
) => {
	const classes = [];
	if (playerReady) classes.push('c-video-hero__video-wrapper--ready');
	if (type === currentType)
		classes.push('c-video-hero__video-wrapper--active');
	if (hidePoster) {
		classes.push('c-video-hero__video-wrapper--hide-poster');
	}
	return classes;
};

// Update calls to use the new utility function
const dynamicBgVideoClass = computed(() =>
	dynamicVideoClass(
		vimeoBgPlayerReady.value,
		'bg',
		playbackType.value,
		hidePoster.value
	)
);
const dynamicFullVideoClass = computed(() =>
	dynamicVideoClass(
		vimeoPlayerReady.value,
		'full',
		playbackType.value,
		hidePoster.value
	)
);

// Video event handling
const onReady = () => {
	vimeoPlayerReady.value = true;
};

const onBgReady = async () => {
	vimeoBgPlayerReady.value = true;
};

const onPlaying = () => {
	if (hidePoster.value === false) {
		hidePoster.value = true;
	}
};

// Action queue
const actionQueue: (() => Promise<void>)[] = [];
let isProcessing = false;

const processQueue = async () => {
	if (isProcessing) return;

	isProcessing = true;
	try {
		while (actionQueue.length > 0) {
			const action = actionQueue.shift();
			if (!action) break;
			await action(); // Wait for each action to finish
		}
	} catch (error) {
		console.error('Action failed', error);
	} finally {
		isProcessing = false;
	}
};

const enqueueAction = (action: () => Promise<void>) => {
	actionQueue.push(action);
	processQueue();
};

// Lifecycle hooks
onMounted(() => {
	loaded.value = true;
	showBackgroundVideo();
	window.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleEscapeKey);
});

// Video Control Methods
const playWithTimeout = async (
	player: VimeoPlayerInstance,
	timeoutMs = 1000
) => {
	return new Promise<void>((resolve, reject) => {
		let timeout = setTimeout(() => {
			reject(new Error('Vimeo play() timed out'));
		}, timeoutMs);

		player
			.play()
			.then(() => {
				clearTimeout(timeout);
				resolve();
			})
			.catch((err: Error) => {
				clearTimeout(timeout);
				reject(err);
			});
	});
};

const pauseWithTimeout = async (
	player: VimeoPlayerInstance,
	timeoutMs = 500
) => {
	return new Promise<void>((resolve, reject) => {
		let timeout = setTimeout(() => {
			console.warn('Vimeo pause() timed out');
			reject(new Error('Vimeo pause() timed out'));
		}, timeoutMs);

		player
			.pause()
			.then(() => {
				clearTimeout(timeout);
				resolve();
			})
			.catch((err: Error) => {
				clearTimeout(timeout);
				reject(err);
			});
	});
};

const errorLog = ref<Error[]>([]);

const bgPlay = async (userTriggered: boolean = true) => {
	enqueueAction(async () => {
		try {
			if (vimeoBgPlayer.value && 'play' in vimeoBgPlayer.value) {
				await playWithTimeout(
					vimeoBgPlayer.value as VimeoPlayerInstance
				);
			}
		} catch (err) {
			errorLog.value.push(err as Error);
		}
		updatePlayState(true);
		if (userTriggered) {
			userPlayState.value = 'play';
		}
	});
};

const bgPause = async (userTriggered: boolean = true) => {
	enqueueAction(async () => {
		try {
			if (vimeoBgPlayer.value && 'pause' in vimeoBgPlayer.value) {
				await pauseWithTimeout(
					vimeoBgPlayer.value as VimeoPlayerInstance
				);
			}
		} catch (err) {
			errorLog.value.push(err as Error);
		}
		updatePlayState(false);
		if (userTriggered) {
			userPlayState.value = 'pause';
		}
	});
};

const switchToFullPlayback = (scrollIntoView = false) => {
	playbackType.value = 'full';

	if (scrollIntoView) {
		vimeoPlayer.value?.$el.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	}
};

const timeupdateHandler = (event: {
	seconds: number;
	percent: number;
	duration: number;
}) => {
	videoProgress.value = event.percent;
};

const updatePlayState = (isPlaying: boolean) => {
	progressButton.value?.setButtonState(isPlaying);
};

const showBackgroundVideo = async () => {
	if (vimeoPlayer.value) {
		vimeoPlayer.value?.pause();
	}
	playbackType.value = 'bg';
	if (userPlayState.value !== 'pause') {
		await bgPlay(false);
	}
};

const showFullVideo = async () => {
	await bgPause(false);
	vimeoPlayer.value?.mute();
	vimeoPlayer.value?.play();
	vimeoPlayer.value?.unmute();
};

// Event handler for Escape key
const handleEscapeKey = (event: KeyboardEvent) => {
	if (event.key === 'Escape' && playbackType.value === 'full') {
		showBackgroundVideo();
	}
};

watch(playbackType, (newVal: 'bg' | 'full') => {
	if (newVal === 'full') {
		showFullVideo();
	}
});

if (import.meta.env.MODE === 'test') {
	(window as Window & {
		__videoHeroDebug__?: { errorLog: typeof errorLog };
	}).__videoHeroDebug__ = { errorLog };
}
</script>

<style>
@import '@coloplast/design-system/dist/components/button/ds-button-play-progress';
@import '@coloplast/design-system/dist/components/underscore/ds-underscore';
</style>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-video-hero {
	--video-aspect-ratio: 16 / 9;
	--quote-display: none;
	--max-quote-length: 20ch;

	aspect-ratio: var(--video-aspect-ratio);
	block-size: auto;
	inline-size: 100%;
	overflow: hidden;
	position: relative;

	@media (min-width: ds-breakpoint('md')) {
		--quote-display: block;
	}

	@media (min-width: ds-breakpoint('lg')) {
		--max-quote-length: 27ch;
	}

	&:has(.ds-block--bg-neutral) {
		background-color: ds-color('bg', 'block-neutral');
	}

	@media screen and (prefers-reduced-motion: reduce) {
		&__block {
			transition: none;
		}
	}

	&__block {
		--block-padding-start: #{ds-space(8)};
		--block-padding-end: #{ds-space(8)};
		block-size: 100%;
		box-sizing: border-box;
		padding-block-end: var(--block-padding-end);
		padding-block-start: var(--block-padding-start);

		@media (min-width: ds-breakpoint('md')) {
			--block-padding-start: var(--block-padding-block-start);
			--block-padding-end: var(--block-padding-block-end);
		}

		@media (max-width: ds-breakpoint("lg")) {
			--block-bg: #{ds-color(bg, dark)};
		}

		.ds-block__content {
			block-size: 100%;
		}
	}

	@media screen and (prefers-reduced-motion: reduce) {
		&__poster-img {
			transition: none;
		}
	}

	&__poster-img {
		block-size: 100%;
		inline-size: 100%;
		inset: 0;
		object-fit: cover;
		position: absolute;
		transition: 0.3s opacity ease;
	}

	&__container {
		block-size: 100%;
		display: flex;
		max-block-size: 70vh;
		align-items: flex-end;
	}

	&__mask {
		block-size: 100%;
		inline-size: 100%;
		inset: 0;
		pointer-events: none;
		position: absolute;
	}

	&__video-wrapper,
	&__video {
		block-size: 100%;
		inline-size: 100%;
		inset: 0;
		object-fit: cover;
		position: absolute;
	}

	&__video-wrapper {
		pointer-events: none;
	}

	&__video-wrapper--active {
		opacity: 1;
		pointer-events: all;
		z-index: 1;
	}

	&__video-wrapper--hide-poster &__poster-img {
		opacity: 0;
		pointer-events: none;
	}

	&__video-overlay {
		background: linear-gradient(90deg, #000 22.8%, rgba(0, 0, 0, 0) 46.47%);
		block-size: 100%;
		inline-size: 100%;
		inset: 0;
		opacity: 0.4;
		position: absolute;
		z-index: 2;
	}

	&__close:is(.ds-icon-button) {
		--close-button-margin: #{ds-space(3)};

		inset-block-start: var(--close-button-margin);
		inset-inline-end: var(--close-button-margin);
		position: absolute;
		transform: translate3d(0px, 0px, 0px);
		z-index: -1;

		@media (min-width: ds-breakpoint('md')) {
			--close-button-margin: #{ds-space(5)};
		}
	}

	&__video-wrapper--active &__close {
		z-index: 20;
	}

	@media screen and (prefers-reduced-motion: reduce) {
		&__video {
			transition: none;
		}
	}

	&__vimeo {
		block-size: 100%;
		inline-size: 100%;
		inset: 0;
		position: absolute;
		scroll-margin-block-start: 210px;

		iframe {
			block-size: 100%;
			inline-size: 100%;
		}
	}

	&__video {
		@media screen and (prefers-reduced-motion: reduce) {
			iframe {
				transition: none;
			}
		}

		iframe {
			block-size: 100%;
			inline-size: 100%;
			inset: 0;
			object-fit: cover;
			opacity: 0;
			position: absolute;
			transition: 0.1s opacity ease;
			transition-delay: 0.5s;
		}

		&--full {
			opacity: 0;
			pointer-events: all;
		}
	}

	&__video-wrapper--ready &__video iframe {
		opacity: 1;
	}

	@media screen and (prefers-reduced-motion: reduce) {
		&__content {
			transition: none;
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: ds-gap(7);
		position: relative;
		z-index: 10;
		inline-size: 100%;
		max-inline-size: 536px;
	}

	&__text {
		display: flex;
		flex-direction: column;
		gap: ds-gap(6);
	}

	@media screen and (prefers-reduced-motion: reduce) {
		&__poster {
			transition: none;
		}
	}

	&__tag,
	&__title {
		color: ds-color('fg', 'on-color');
		margin: 0;
	}

	&__title {
		text-wrap: balance;
	}

	&__cta-placeholder {
		visibility: hidden;
	}

	&__progress-button {
		inset-block-end: ds-space(8);
		inset-inline-end: ds-space(8);
		position: absolute;
		z-index: 20;
	}

	@media screen and (prefers-reduced-motion: reduce) {
		.content-enter-active {
			transition: none;
		}
	}

	.content-enter-active {
		transition: all 1s ease;
		transition-delay: 0.3s;
	}
	@media screen and (prefers-reduced-motion: reduce) {
		.content-leave-active {
			transition: none;
		}
	}

	.content-leave-active {
		transition: all 0.5s ease;
	}

	.content-enter-from,
	.content-leave-to {
		opacity: 0;
	}
}
</style>
